import {
  createAsyncThunk,
  createSelector,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';
import {
  getVenueProductCategories,
  getVenueProducts,
  observeVenueProductCategories,
  observeVenueProducts,
} from 'firebase/productFirestore';
import { getVenue } from 'firebase/venueFirestore';
import { accountCleared } from '../actions';
import type { AppDispatch, RootState } from '../store';

type LoadingState = 'idle' | 'pending' | 'succeeded' | 'failed';

interface VenueState {
  venueId: string | null;
  venueData: Pagcomanda.Venue | null;
  loadingVenue: LoadingState;
  venueRequestId: string | null;

  products: Array<{ id: string; data: Pagcomanda.Product }>;
  productsAvailable: Array<{ id: string; data: Pagcomanda.Product }>;
  productCatalog: Array<{ id: string; data: Pagcomanda.Product }>;
  loadingProducts: LoadingState;
  productsRequestId: string | null;

  categories: Array<{ id: string; data: Pagcomanda.ProductCategory }>;
  productsCategories: Array<{ id: string; data: Pagcomanda.ProductCategory }>;
  loadingCategories: LoadingState;
  categoriesRequestId: string | null;
}

const deriveProducts = (products: Array<{ id: string; data: Pagcomanda.Product }>) => {
  const productsAvailable = products.filter((p) => !p.data?.hideOnMenu);
  return {
    products,
    productsAvailable,
    productCatalog: productsAvailable,
  };
};

const deriveCategories = (categories: Array<{ id: string; data: Pagcomanda.ProductCategory }>) => {
  const sorted = [...categories].sort((a, b) => {
    const orderA = a.data?.order ?? 0;
    const orderB = b.data?.order ?? 0;
    return orderA > orderB ? 1 : -1;
  });
  return {
    categories: sorted,
    productsCategories: sorted,
  };
};

const initialState: VenueState = {
  venueId: null,
  venueData: null,
  loadingVenue: 'idle',
  venueRequestId: null,

  products: [],
  productsAvailable: [],
  productCatalog: [],
  loadingProducts: 'idle',
  productsRequestId: null,

  categories: [],
  productsCategories: [],
  loadingCategories: 'idle',
  categoriesRequestId: null,
};

let currentSubscribedVenueId: string | null = null;
let activeUnsubscribers: Array<() => void> = [];

export const stopVenueSubscription = () => {
  activeUnsubscribers.forEach((unsubscribe) => unsubscribe());
  activeUnsubscribers = [];
  currentSubscribedVenueId = null;
};

export const loadCategories = createAsyncThunk(
  'venue/loadCategories',
  async (venueId: string) => getVenueProductCategories(venueId, { publishedOnly: true })
    .then((docs) => docs.map((doc) => ({
      id: doc.id,
      data: doc.data(),
    }))),
);

export const loadProducts = createAsyncThunk(
  'venue/loadProducts',
  async (venueId: string) => getVenueProducts(venueId, { publishedOnly: true })
    .then((docs) => docs.map((doc) => ({
      id: doc.id,
      data: doc.data(),
    }))),
);

export const loadVenue = createAsyncThunk(
  'venue/loadVenue',
  async (venueId: string, thunkApi) => getVenue(venueId)
    .then((doc) => {
      if (!doc.exists() || !doc.data()) {
        throw new Error('Venue not found.');
      }

      thunkApi.dispatch(loadCategories(venueId));
      thunkApi.dispatch(loadProducts(venueId));

      return doc.data() as Pagcomanda.Venue;
    }),
);

export const venueSlice = createSlice({
  name: 'venue',
  initialState,
  reducers: {
    venueSubscriptionStarted: (state, action: PayloadAction<string>) => {
      if (state.venueId === action.payload) {
        return state;
      }
      return {
        ...state,
        ...initialState,
        venueId: action.payload,
        loadingProducts: 'pending',
        loadingCategories: 'pending',
      };
    },
    categoriesReceived: (
      state,
      action: PayloadAction<{
        venueId: string;
        categories: Array<{ id: string; data: Pagcomanda.ProductCategory }>;
      }>,
    ) => {
      if (state.venueId && state.venueId !== action.payload.venueId) return state;
      const derived = deriveCategories(action.payload.categories);
      return {
        ...state,
        ...derived,
        loadingCategories: 'succeeded',
      };
    },
    categoriesFailed: (state, action: PayloadAction<{ venueId: string }>) => {
      if (state.venueId && state.venueId !== action.payload.venueId) return state;
      return {
        ...state,
        loadingCategories: 'failed',
      };
    },
    productsReceived: (
      state,
      action: PayloadAction<{
        venueId: string;
        products: Array<{ id: string; data: Pagcomanda.Product }>;
      }>,
    ) => {
      if (state.venueId && state.venueId !== action.payload.venueId) return state;
      const derived = deriveProducts(action.payload.products);
      return {
        ...state,
        ...derived,
        loadingProducts: 'succeeded',
      };
    },
    productsFailed: (state, action: PayloadAction<{ venueId: string }>) => {
      if (state.venueId && state.venueId !== action.payload.venueId) return state;
      return {
        ...state,
        loadingProducts: 'failed',
      };
    },
    logout: (state) => {
      stopVenueSubscription();
      return {
        ...state,
        ...initialState,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(accountCleared, () => {
      stopVenueSubscription();
      return initialState;
    });
    builder.addCase(loadVenue.pending, (state, action) => ({
      ...state,
      ...initialState,
      venueId: action.meta.arg,
      loadingVenue: 'pending',
      venueRequestId: action.meta.requestId,
    }));
    builder.addCase(loadVenue.fulfilled, (state, action) => (
      state.venueRequestId === action.meta.requestId
        ? { ...state, loadingVenue: 'succeeded', venueData: action.payload }
        : state
    ));
    builder.addCase(loadVenue.rejected, (state, action) => (
      state.venueRequestId === action.meta.requestId
        ? { ...state, loadingVenue: 'failed' }
        : state
    ));

    builder.addCase(loadCategories.pending, (state, action) => (
      state.venueId === action.meta.arg
        ? {
          ...state,
          loadingCategories: 'pending',
          categoriesRequestId: action.meta.requestId,
        }
        : state
    ));
    builder.addCase(loadCategories.fulfilled, (state, action) => {
      if (state.categoriesRequestId !== action.meta.requestId) return state;
      const derived = deriveCategories(action.payload);
      return {
        ...state,
        loadingCategories: 'succeeded',
        ...derived,
      };
    });
    builder.addCase(loadCategories.rejected, (state, action) => (
      state.categoriesRequestId === action.meta.requestId
        ? { ...state, loadingCategories: 'failed' }
        : state
    ));

    builder.addCase(loadProducts.pending, (state, action) => (
      state.venueId === action.meta.arg
        ? {
          ...state,
          loadingProducts: 'pending',
          productsRequestId: action.meta.requestId,
        }
        : state
    ));
    builder.addCase(loadProducts.fulfilled, (state, action) => {
      if (state.productsRequestId !== action.meta.requestId) return state;
      const derived = deriveProducts(action.payload);
      return {
        ...state,
        loadingProducts: 'succeeded',
        ...derived,
      };
    });
    builder.addCase(loadProducts.rejected, (state, action) => (
      state.productsRequestId === action.meta.requestId
        ? { ...state, loadingProducts: 'failed' }
        : state
    ));
  },
});

export const {
  categoriesFailed,
  categoriesReceived,
  logout,
  productsFailed,
  productsReceived,
  venueSubscriptionStarted,
} = venueSlice.actions;

export const subscribeToVenueMenu = (venueId: string) => (
  dispatch: AppDispatch,
) => {
  if (!venueId) return () => {};

  if (currentSubscribedVenueId === venueId) {
    return () => {};
  }

  stopVenueSubscription();
  currentSubscribedVenueId = venueId;

  dispatch(venueSubscriptionStarted(venueId));

  const unsubCategories = observeVenueProductCategories(
    venueId,
    (snapshot) => {
      const categories = snapshot.docs.map((doc) => ({
        id: doc.id,
        data: doc.data(),
      }));
      dispatch(categoriesReceived({ venueId, categories }));
    },
    () => {
      dispatch(categoriesFailed({ venueId }));
    },
  );

  const unsubProducts = observeVenueProducts(
    venueId,
    (snapshot) => {
      const products = snapshot.docs.map((doc) => ({
        id: doc.id,
        data: doc.data(),
      }));
      dispatch(productsReceived({ venueId, products }));
    },
    () => {
      dispatch(productsFailed({ venueId }));
    },
  );

  activeUnsubscribers = [unsubCategories, unsubProducts];

  return () => {
    // Listener intentionally preserved across session navigations
  };
};

export const subscribeToVenue = subscribeToVenueMenu;

export const selectLoadingVenue = (state: RootState) => state.venue.loadingVenue;
export const selectVenue = (state: RootState) => state.venue;

export const selectLoadingCategories = (state: RootState) => state.venue.loadingCategories;
export const selectProductCategories = (state: RootState) => state.venue.categories;
export const selectProductsCategories = (state: RootState) => state.venue.productsCategories;

export const selectLoadingProducts = (state: RootState) => state.venue.loadingProducts;
export const selectProducts = (state: RootState) => state.venue.products;
export const selectProductsAvailable = (state: RootState) => state.venue.productsAvailable;
export const selectProductCatalog = (state: RootState) => state.venue.productCatalog;
export const selectFavoriteProducts = createSelector([
  selectProducts,
], (
  products,
) => products.filter((p) => p.data.isFavorite));

export const selectCategoryId = (state: RootState, categoryId: string) => categoryId;
export const selectProductsByCategoryId = createSelector([
  selectCategoryId,
  selectProducts,
], (
  categoryId,
  products,
) => products.filter((p) => p.data.categoryId === categoryId));

export const selectProductId = (state: RootState, productId: string) => productId;
export const selectProductById = createSelector([
  selectProductId,
  selectProducts,
], (
  productId,
  products,
) => products.find((p) => p.id === productId));

export const selectHasLoadingActivity = createSelector([
  selectLoadingVenue,
  selectLoadingCategories,
  selectLoadingProducts,
], (
  loadingVenue,
  loadingCategories,
  loadingProducts,
) => [loadingVenue, loadingCategories, loadingProducts].some((v) => v === 'pending'));

export default venueSlice.reducer;
